// frontend/src/components/staff/StaffShiftDetailModal.js
//
// Workforce — SCHED-SLICE1-G read-only shift detail modal.
//
// Visual spec: docs/design/workforce-scheduling-slice-1-design.md §9.3
// Story:       docs/backlog/SCHED-SLICE1-G-staff-schedule-view-ui.md
//                Acceptance Scenario 2 — read-only Detail modal
//
// READ-ONLY by design — no edit/delete affordances per AC Scenario 2.
// The only action is Close (X button + footer button + Esc key).
//
// All timestamps render in America/Phoenix per CLAUDE.md global rule.

import React from "react";
import {
  formatRoleLabel,
  formatShiftDateLine,
  formatShiftDuration,
  formatShiftTimeRange,
  formatShiftStamp,
  getRoleColor,
} from "./staffScheduleUtils";
import "./StaffShiftDetailModal.css";

/**
 * Read-only shift detail modal for the staff schedule view.
 *
 * @param {object}  props
 * @param {boolean} props.open
 * @param {object|null} props.shift  Shift object (or null when closed)
 * @param {() => void} props.onClose
 */
const StaffShiftDetailModal = ({ open, shift, onClose }) => {
  const closeRef = React.useRef(null);

  // Focus the Close button on open for keyboard a11y.
  React.useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      if (closeRef.current) closeRef.current.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [open]);

  // Close on Esc.
  React.useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose && onClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open || !shift) return null;

  const role = getRoleColor(shift.roleCode);
  const roleLabel = formatRoleLabel(shift.roleCode);
  const dateLine = formatShiftDateLine(shift.startTime);
  const timeRange = formatShiftTimeRange(shift.startTime, shift.endTime);
  const duration = formatShiftDuration(shift.startTime, shift.endTime);
  const updatedStamp = shift.updatedAt
    ? formatShiftStamp(shift.updatedAt)
    : null;

  const handleBackdropClick = (e) => {
    // Only close if the click target is the backdrop itself.
    if (e.target === e.currentTarget) {
      onClose && onClose();
    }
  };

  return (
    <div
      className="staff-shift-detail__backdrop"
      onClick={handleBackdropClick}
      data-testid="staff-shift-detail-backdrop"
    >
      <div
        className="staff-shift-detail__modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="staff-shift-detail-heading"
        data-testid="staff-shift-detail-modal"
      >
        <header className="staff-shift-detail__header">
          <h2
            className="staff-shift-detail__heading"
            id="staff-shift-detail-heading"
          >
            Shift detail
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="staff-shift-detail__close"
            onClick={onClose}
            aria-label="Close shift detail"
            data-testid="staff-shift-detail-close"
          >
            &times;
          </button>
        </header>

        <div className="staff-shift-detail__body">
          <p className="staff-shift-detail__date">{dateLine}</p>
          <p className="staff-shift-detail__time">{timeRange}</p>

          <div className="staff-shift-detail__row">
            <span className="staff-shift-detail__label">Role:</span>
            <span
              className="staff-shift-detail__role-pill"
              style={{ background: role.bg, color: role.text }}
              data-testid="staff-shift-detail-role"
            >
              {roleLabel}
            </span>
          </div>

          <div className="staff-shift-detail__row">
            <span className="staff-shift-detail__label">Duration:</span>
            <span data-testid="staff-shift-detail-duration">{duration}</span>
          </div>

          {shift.notes ? (
            <div className="staff-shift-detail__notes">
              <p className="staff-shift-detail__label">Notes:</p>
              <p
                className="staff-shift-detail__notes-body"
                data-testid="staff-shift-detail-notes"
              >
                {shift.notes}
              </p>
            </div>
          ) : null}

          {updatedStamp ? (
            <p
              className="staff-shift-detail__updated"
              data-testid="staff-shift-detail-updated"
            >
              Last updated {updatedStamp}
            </p>
          ) : null}
        </div>

        <footer className="staff-shift-detail__footer">
          <button
            type="button"
            className="staff-shift-detail__close-btn"
            onClick={onClose}
            data-testid="staff-shift-detail-close-btn"
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  );
};

export default StaffShiftDetailModal;
